import { useState } from "react";
import Layout from "../../components/Layout";
import {
  Bot, Zap, Sparkles, Search, Filter, Pause, Play,
  Package2, PhoneCall, Mail, MessageSquare, ShoppingBag, TrendingUp,
  DollarSign, Users2, RotateCcw, Clock, LineChart, Percent,
  ClipboardList, AlertTriangle, Gift, Radar,
} from "lucide-react";

/**
 * Lara AI · Agent Library
 *
 * Scaffolding view — shows the roadmap of AI agents that will one day be wired
 * up through Lara AI's Workflow Builder. Every card is currently INERT.
 * Each agent will be enabled individually as it's built out.
 *
 * When we go live: flip `status` from "planned" → "beta" → "live" per agent,
 * and later hook up its `attaches_to` stages / triggers in the workflow engine.
 */

const AGENTS = [
  // ── Stage-attached agents (in workflow order) ──────────────────────
  {
    key: "next_order_predictor",
    name: "Next-Order Predictor",
    icon: TrendingUp,
    color: "amber",
    category: "outreach",
    tagline: "Predicts when a top customer is due for their next order.",
    detail: "Scans order cadence + product replenishment cycles + seasonal patterns to identify customers about to reorder. Feeds AI Engage.",
    attaches_to: ["ai_engage"],
    triggers: ["cron_daily"],
    status: "planned",
  },
  {
    key: "intent_classifier",
    name: "Intent Classifier",
    icon: Radar,
    color: "fuchsia",
    category: "conversation",
    tagline: "Categorises inbound customer replies in real time.",
    detail: "Reads incoming WhatsApp/email/call transcripts and tags: quote-request · question · complaint · price-check · not-interested. Powers CX Replied handover briefs.",
    attaches_to: ["ai_cx_replied"],
    triggers: ["webhook_incoming_message"],
    status: "planned",
  },
  {
    key: "enquiry_router",
    name: "Enquiry Router",
    icon: ClipboardList,
    color: "sky",
    category: "conversation",
    tagline: "Preps handover briefs when human takes over.",
    detail: "At the first two-way contact, drafts a one-page brief for the account manager: what the customer asked, likely quote value, historic LTV, similar past interactions.",
    attaches_to: ["first_contact"],
    triggers: ["stage_change"],
    status: "planned",
  },
  {
    key: "stock_checker",
    name: "Stock & Materials Drafter",
    icon: Package2,
    color: "purple",
    category: "quoting",
    tagline: "Turns specs into a stock-checked materials list.",
    detail: "Reads the customer's spec (from email/WA/notes), matches products from the catalogue, checks live inventory, drafts the line-items into a Talmia quote for AM review.",
    attaches_to: ["do_quote"],
    triggers: ["stage_change"],
    status: "planned",
  },
  {
    key: "objection_detector",
    name: "Objection Detector",
    icon: AlertTriangle,
    color: "orange",
    category: "quoting",
    tagline: "Spots pricing/competitor objections in replies.",
    detail: "Analyses replies to a sent quote — surfaces phrases like 'cheaper elsewhere', 'too expensive', 'need to think'. Flags to AM with suggested discount room + counter-lines.",
    attaches_to: ["quote_sent"],
    triggers: ["webhook_incoming_message"],
    status: "planned",
  },
  {
    key: "re_engagement",
    name: "Silent-Customer Nudger",
    icon: Clock,
    color: "amber",
    category: "outreach",
    tagline: "Timed nudges when a deal goes quiet.",
    detail: "Counts silent days on HOLD / NO ANSWER — sends templated re-engagement WhatsApp/email at day 2/5/10 with different angles. Gives up gracefully after N attempts and moves to Lost.",
    attaches_to: ["hold_no_answer"],
    triggers: ["cron_hourly", "stage_change"],
    status: "planned",
  },
  {
    key: "payment_chaser",
    name: "Payment Chaser",
    icon: DollarSign,
    color: "yellow",
    category: "operations",
    tagline: "Automated payment reminders + Xero invoice sync.",
    detail: "Polls Xero for outstanding invoices, matches to Talmia's Waiting Payment stage, sends escalating reminders (email → SMS → AM ping). Escalates disputes automatically.",
    attaches_to: ["waiting_payment"],
    triggers: ["cron_daily", "xero_webhook"],
    status: "planned",
  },
  {
    key: "review_requester",
    name: "Review Requester",
    icon: Gift,
    color: "emerald",
    category: "post-sale",
    tagline: "Timed review requests after a Won order.",
    detail: "N days after delivery (configurable per SKU), sends the customer a review request tuned to platform (Trustpilot / Google). Also suggests complementary products from co-purchase data.",
    attaches_to: ["won_ordered"],
    triggers: ["stage_change", "delivery_confirmed"],
    status: "planned",
  },
  {
    key: "resurrection_monitor",
    name: "Resurrection Monitor",
    icon: RotateCcw,
    color: "rose",
    category: "reactivation",
    tagline: "Watches archived accounts for reactivation signals.",
    detail: "Monitors ex-customers for Companies-House updates, industry-news mentions, competitor churn signals. Pings AM when a lost high-value account shows life.",
    attaches_to: ["lost_archive"],
    triggers: ["cron_weekly", "external_signal"],
    status: "planned",
  },
  {
    key: "recycler",
    name: "Recycler",
    icon: Sparkles,
    color: "cyan",
    category: "reactivation",
    tagline: "Monthly win-back campaigns for dormant customers.",
    detail: "Pulls customers idle 6+ months, builds a targeted win-back sequence with a controlled incentive, feeds revived deals back into AI Engage.",
    attaches_to: ["ai_recycle"],
    triggers: ["cron_monthly"],
    status: "planned",
  },
  // ── Utility / cross-stage agents ────────────────────────────────────
  {
    key: "price_benchmarker",
    name: "Price Benchmarker",
    icon: LineChart,
    color: "teal",
    category: "intelligence",
    tagline: "Watches competitor SKU prices.",
    detail: "Scrapes competitor product listings on a schedule, flags SKU price gaps > X%, feeds Objection Detector with fresh counter-context.",
    attaches_to: ["quote_sent", "do_quote"],
    triggers: ["cron_daily"],
    status: "planned",
  },
  {
    key: "content_generator",
    name: "Copy Generator",
    icon: MessageSquare,
    color: "fuchsia",
    category: "outreach",
    tagline: "Drafts personalised email / SMS / WhatsApp copy.",
    detail: "Given a customer + goal (nurture / win-back / follow-up / upsell), drafts 3 tone variants for AM to pick. Uses the customer's LTV, tier, last-order details.",
    attaches_to: ["ai_engage", "hold_no_answer", "ai_recycle"],
    triggers: ["manual", "cron_daily"],
    status: "planned",
  },
  {
    key: "sentiment_watcher",
    name: "Sentiment Watcher",
    icon: Bot,
    color: "purple",
    category: "intelligence",
    tagline: "Cross-channel sentiment + escalation.",
    detail: "Continuously analyses replies across calls, WhatsApp, email, forms. When sentiment turns Negative on a high-LTV account, escalates to Joanna / AM immediately.",
    attaches_to: ["ai_cx_replied", "first_contact", "quote_sent"],
    triggers: ["webhook_incoming_message"],
    status: "planned",
  },
  {
    key: "ticket_triager",
    name: "Ticket Triager",
    icon: ClipboardList,
    color: "orange",
    category: "operations",
    tagline: "Sorts support tickets before humans see them.",
    detail: "Ranks incoming tickets by urgency (Elite customer + delivery issue > lead + generic question), auto-answers common FAQs from the KB, escalates the rest.",
    attaches_to: ["first_contact"],
    triggers: ["webhook_incoming_ticket"],
    status: "planned",
  },
  {
    key: "loss_analyst",
    name: "Loss Analyst (Joanna's brief)",
    icon: LineChart,
    color: "rose",
    category: "intelligence",
    tagline: "Weekly debrief of what we lost and why.",
    detail: "Every Monday, writes a summary for Joanna: which deals moved to Lost this week, common objection themes, patterns by AM, suggested playbook tweaks.",
    attaches_to: ["lost_archive"],
    triggers: ["cron_weekly"],
    status: "planned",
  },
];

const CATEGORIES = [
  { key: "all",           label: "All",           color: "text-slate-300" },
  { key: "outreach",      label: "Outreach",      color: "text-fuchsia-300" },
  { key: "conversation",  label: "Conversation",  color: "text-sky-300" },
  { key: "quoting",       label: "Quoting",       color: "text-purple-300" },
  { key: "operations",    label: "Operations",    color: "text-amber-300" },
  { key: "post-sale",     label: "Post-sale",     color: "text-emerald-300" },
  { key: "reactivation",  label: "Reactivation",  color: "text-rose-300" },
  { key: "intelligence",  label: "Intelligence",  color: "text-teal-300" },
];

const COLOR_TOKEN = {
  slate:   "border-slate-500/40 bg-slate-500/[0.05] text-slate-300",
  amber:   "border-amber-500/40 bg-amber-500/[0.05] text-amber-300",
  fuchsia: "border-fuchsia-500/40 bg-fuchsia-500/[0.05] text-fuchsia-300",
  sky:     "border-sky-500/40 bg-sky-500/[0.05] text-sky-300",
  purple:  "border-purple-500/40 bg-purple-500/[0.05] text-purple-300",
  orange:  "border-orange-500/40 bg-orange-500/[0.05] text-orange-300",
  yellow:  "border-yellow-500/40 bg-yellow-500/[0.05] text-yellow-200",
  emerald: "border-emerald-500/40 bg-emerald-500/[0.05] text-emerald-300",
  rose:    "border-rose-500/40 bg-rose-500/[0.05] text-rose-300",
  cyan:    "border-cyan-500/40 bg-cyan-500/[0.05] text-cyan-300",
  teal:    "border-teal-500/40 bg-teal-500/[0.05] text-teal-300",
};

const STATUS_BADGES = {
  planned: { label: "Planned", cls: "bg-slate-500/15 text-slate-400 border-slate-500/30" },
  beta:    { label: "Beta",    cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  live:    { label: "Live",    cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
};

function AgentCard({ agent }) {
  const Icon = agent.icon;
  const cls = COLOR_TOKEN[agent.color] || COLOR_TOKEN.slate;
  const status = STATUS_BADGES[agent.status] || STATUS_BADGES.planned;
  const isLive = agent.status === "live";

  return (
    <div
      className={`rounded-xl border ${cls} p-4 flex flex-col gap-3 opacity-95`}
      data-testid={`lara-agent-card-${agent.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`w-10 h-10 rounded-lg border ${cls} grid place-items-center`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-mono uppercase tracking-widest border ${status.cls}`}>
          {status.label}
        </span>
      </div>
      <div>
        <div className="text-white font-semibold text-sm">{agent.name}</div>
        <div className="text-[12px] text-slate-300 mt-1">{agent.tagline}</div>
      </div>
      <p className="text-[12px] text-slate-400 leading-relaxed">{agent.detail}</p>

      {/* Meta chips */}
      <div className="mt-auto pt-2 border-t border-white/[0.06] space-y-1.5">
        {agent.attaches_to?.length > 0 && (
          <div className="flex flex-wrap gap-1 items-center">
            <span className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mr-1">Stages:</span>
            {agent.attaches_to.map((s) => (
              <span key={s} className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/10 text-slate-300">
                {s}
              </span>
            ))}
          </div>
        )}
        {agent.triggers?.length > 0 && (
          <div className="flex flex-wrap gap-1 items-center">
            <span className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mr-1">Triggers:</span>
            {agent.triggers.map((t) => (
              <span key={t} className="text-[9.5px] font-mono px-1.5 py-0.5 rounded bg-white/[0.04] border border-white/10 text-slate-400">
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Inert toggle */}
      <div className="flex items-center justify-between pt-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
          {isLive ? "Running" : "Not wired"}
        </span>
        <button
          type="button"
          disabled
          data-testid={`lara-agent-toggle-${agent.key}`}
          aria-disabled="true"
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-mono uppercase tracking-widest border cursor-not-allowed ${
            isLive ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-white/10 bg-white/[0.03] text-slate-500"
          }`}
          title="Wiring TBD"
        >
          {isLive ? <Play className="w-3 h-3" /> : <Pause className="w-3 h-3" />}
          {isLive ? "On" : "Off"}
        </button>
      </div>
    </div>
  );
}

export default function AgentLibrary() {
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");

  const filtered = AGENTS.filter((a) => {
    if (category !== "all" && a.category !== category) return false;
    if (q) {
      const s = q.toLowerCase();
      return (a.name + " " + a.tagline + " " + a.detail).toLowerCase().includes(s);
    }
    return true;
  });

  const stats = {
    total:   AGENTS.length,
    planned: AGENTS.filter((a) => a.status === "planned").length,
    beta:    AGENTS.filter((a) => a.status === "beta").length,
    live:    AGENTS.filter((a) => a.status === "live").length,
  };

  return (
    <Layout>
    <div className="space-y-6" data-testid="lara-agent-library">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-purple-400 font-mono mb-1 flex items-center gap-1.5">
            <Bot className="w-3 h-3" /> Lara AI · Agent Library
          </div>
          <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-3">
            Agents
            <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-400 border border-white/10">
              Scaffold
            </span>
          </h1>
          <div className="text-slate-400 text-sm mt-1 max-w-2xl">
            The roadmap of Talmia AI agents. Each one is a scoped worker with a defined
            stage, trigger and behaviour. Wiring lives inside Lara AI&rsquo;s Workflow Builder —
            <span className="text-slate-500"> TBD</span>.
          </div>
        </div>

        {/* Stat strip */}
        <div className="flex items-stretch gap-2 text-sm">
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[80px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Total</div>
            <div className="text-white font-semibold tabular-nums text-lg">{stats.total}</div>
          </div>
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[80px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Planned</div>
            <div className="text-slate-300 font-semibold tabular-nums text-lg">{stats.planned}</div>
          </div>
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[80px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Beta</div>
            <div className="text-amber-300 font-semibold tabular-nums text-lg">{stats.beta}</div>
          </div>
          <div className="px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] min-w-[80px]">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Live</div>
            <div className="text-emerald-300 font-semibold tabular-nums text-lg">{stats.live}</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-[420px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search agents…"
            data-testid="lara-agent-search"
            className="w-full pl-10 pr-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 focus:border-purple-400/60 focus:bg-white/[0.05] outline-none text-sm text-white placeholder:text-slate-500"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-slate-500" />
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              data-testid={`lara-agent-cat-${c.key}`}
              className={`px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-widest border transition-colors ${
                category === c.key
                  ? "bg-white/10 border-white/25 text-white"
                  : "border-white/10 text-slate-400 hover:text-white"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="lara-agent-grid">
        {filtered.length === 0 && (
          <div className="col-span-full p-12 text-center text-slate-500 text-sm border border-dashed border-white/10 rounded-lg">
            No agents match this filter.
          </div>
        )}
        {filtered.map((a) => <AgentCard key={a.key} agent={a} />)}
      </div>

      <div className="text-[10.5px] text-slate-600 font-mono flex items-center gap-1.5">
        <Zap className="w-3 h-3" />
        Wiring pattern: <code className="text-slate-500">trigger → agent → action → audit-log</code>. Full spec in
        <code className="text-slate-500 ml-1">/app/memory/CRM_AI_WORKFLOW.md</code>.
      </div>
    </div>
    </Layout>
  );
}
